/**
 * OfflineResetService
 *
 * User-facing controls to clear offline data that has piled up in IndexedDB.
 * Two operations, matching the two real situations:
 *
 *  - discardPending(): drop only the *unsynced* locally-created/edited records
 *    (and the queued-mutation queues) across every offline table, while KEEPING
 *    the cached server data so the app stays usable offline. Use this when the
 *    pending queue is stuck or references entities that no longer exist on the
 *    server (e.g. after a data reset/re-seed).
 *
 *  - resetAllOfflineData(): nuke the entire offline database (pending + cached)
 *    and reload, forcing a clean re-download from the server. Use this when the
 *    cached copy itself is stale.
 *
 * Table classification is derived dynamically from the Dexie schema so new
 * offline tables are covered automatically:
 *  - LOCAL_ONLY_TABLES    — local-only working state that has NO sync path at all
 *                           even though it carries an `is_synced` property. These
 *                           are never "pending upload" and must never be counted
 *                           or discarded. Checked FIRST, ahead of the two rules
 *                           below, because such a table can otherwise be swept up
 *                           by them purely by accident of its schema.
 *  - QUEUE_TABLES         — pure pending-mutation queues with no is_synced flag;
 *                           every row is pending, so they are cleared entirely.
 *  - any table indexing `is_synced` — dual-purpose (server cache + offline edits);
 *                           only the unsynced rows (is_synced falsy) are removed.
 * Everything else (pure read caches, map tiles) is left untouched by
 * discardPending and only removed by the full reset.
 *
 * When adding a new offline table, classify it deliberately into one of the
 * three buckets above — do not rely on the `is_synced` index to do it for you.
 */

import { db } from './db';
import { storageQuotaService } from './storage-quota.service';

/** Tables that are pure pending queues (no is_synced column). */
const QUEUE_TABLES = new Set(['pendingMutations', 'ics214RosterPending']);

/**
 * Tables that are LOCAL-ONLY working state: they index `is_synced` but have no
 * sync path whatsoever, so the flag is written 0 on insert and never flipped.
 *
 * WHY THIS EXISTS: `formDrafts` holds the auto-saved, half-finished ICS form the
 * operator currently has open (useFormAutoSave). Without this exclusion,
 * hasSyncFlag() classifies it as dual-purpose and isUnsynced() matches every
 * draft forever — so "Discard pending", whose dialog promises only to drop
 * records "that haven't synced" (i.e. the upload queue), would silently delete
 * the operator's in-progress ICS-213/214 during an incident. It would also make
 * getPendingSummary() report a pending count that can never reach zero, since
 * drafts have nothing to sync to.
 *
 * These rows are still wiped by resetAllOfflineData(), which correctly warns it
 * erases ALL offline data.
 */
const LOCAL_ONLY_TABLES = new Set(['formDrafts']);

/**
 * A row is "pending upload" when it is not confirmed-synced. The app's sync
 * indicator counts pending records with a loose `!is_synced` check, so anything
 * that is not strictly synced (1 / true) — including rows where `is_synced` was
 * left undefined/null — must be treated as pending here too. Otherwise the
 * indicator shows N pending but discard can't clear them (the original bug:
 * server-cached rows with `is_synced === undefined` were counted but skipped).
 */
function isUnsynced(row: { is_synced?: number | boolean }): boolean {
  return row.is_synced !== 1 && row.is_synced !== true;
}

/** Does this Dexie table carry an indexed `is_synced` flag? */
function hasSyncFlag(table: (typeof db.tables)[number]): boolean {
  return !!table.schema.idxByName?.is_synced;
}

export interface PendingSummary {
  /** Total pending records across all offline tables + queues. */
  total: number;
  /** Per-table counts (only tables with >0 pending), for display. */
  byTable: Array<{ table: string; count: number }>;
}

export interface StorageSummary {
  usageBytes: number;
  quotaBytes: number;
  percent: number;
}

class OfflineResetService {
  /**
   * Count pending records across every offline table without mutating anything.
   */
  async getPendingSummary(): Promise<PendingSummary> {
    const byTable: Array<{ table: string; count: number }> = [];
    let total = 0;

    for (const table of db.tables) {
      let count = 0;
      if (LOCAL_ONLY_TABLES.has(table.name)) {
        // Never "pending upload" — must be tested before the is_synced rule.
        count = 0;
      } else if (QUEUE_TABLES.has(table.name)) {
        count = await table.count();
      } else if (hasSyncFlag(table)) {
        count = await table.filter(isUnsynced).count();
      }
      if (count > 0) {
        byTable.push({ table: table.name, count });
        total += count;
      }
    }

    byTable.sort((a, b) => b.count - a.count);
    return { total, byTable };
  }

  /**
   * Discard all pending (unsynced) records and queued mutations, keeping cached
   * server data. Returns the number of records discarded.
   */
  async discardPending(): Promise<number> {
    let cleared = 0;

    // One transaction over every table so a failure can't leave a half-cleared
    // queue. db.tables is accepted by Dexie as the transaction scope.
    await db.transaction('rw', db.tables, async () => {
      for (const table of db.tables) {
        if (LOCAL_ONLY_TABLES.has(table.name)) {
          // Local-only working state (in-progress form drafts): not part of the
          // upload queue, so discarding the queue must leave it alone. Tested
          // first so the is_synced-index rule below can't claim it.
          continue;
        }
        if (QUEUE_TABLES.has(table.name)) {
          cleared += await table.count();
          await table.clear();
        } else if (hasSyncFlag(table)) {
          // Collection.delete() resolves to the number of rows removed.
          cleared += await table.filter(isUnsynced).delete();
        }
      }
    });

    return cleared;
  }

  /**
   * Wipe the entire offline database (pending + all cached data) and reload so
   * Dexie re-opens empty and the app re-downloads everything from the server.
   *
   * NOTE: this clears IndexedDB only. The service-worker HTTP cache (app shell)
   * is intentionally left intact so the app still boots offline; it refreshes
   * itself via the normal SW update flow.
   */
  async resetAllOfflineData(): Promise<void> {
    await db.delete();
    // Re-open so any code awaiting `db` after this (before the reload lands)
    // doesn't throw on a closed connection.
    await db.open();
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  /** Current IndexedDB/storage usage for display next to the controls. */
  async getStorageSummary(): Promise<StorageSummary> {
    const status = await storageQuotaService.checkQuota();
    return {
      usageBytes: status.usageBytes ?? 0,
      quotaBytes: status.quotaBytes ?? 0,
      percent: status.percentUsed ?? 0,
    };
  }
}

export const offlineResetService = new OfflineResetService();
